// ============================================================================
// belepesEmlekezet.js — "emlékezz erre az eszközre", 1. szint (v182, Tamás kérése; docs/ESZKOZ_BELEPES_TERV.md).
//
// CÉL: a saját telefonon ritkán kelljen újra belépni, és ha mégis, a lehető kevesebb koppintással. A Google-lépést ez a szint NEM ugorja át
// (az a 2. szint, eszközhöz kötött belépés, külön döntés); itt azt akadályozzuk meg, hogy SZÜKSÉG LEGYEN rá, és azt mérjük, mikor veszik el a munkamenet:
//  1. tartós tároló kérése (navigator.storage.persist): a böngésző ne törölje a belépést a tárolóból;
//  2. a legutóbbi e-mail-cím megjegyzése (NEM titok, csak az eszközön) és átadása a Google-nak (login_hint): újra belépéskor a fiókválasztó kimarad;
//  3. a munkamenet-elvesztés mérése: ha a program tudja, hogy volt belépés, most mégsincs, és a tag nem lépett ki maga, azt feljegyzi.
// KÖZÖS eszközön (eszkozMod.js "kozos") semmi nem marad meg: sem az e-mail, sem a jelzések.
// A modul tiszta döntéseket tartalmaz (Node-ban tesztelhető); a bekötés a firebaseClient.js-ben és a main.js-ben van.
// ============================================================================

export const EMAIL_KULCS = "l0_utolsoEmail";
export const VOLT_BELEPES_KULCS = "l0_voltBelepes";
export const KILEPETT_KULCS = "l0_kilepett";
export const ELVESZETT_KULCS = "l0_munkamenetElveszett"; // JSON: { at, db }
export const TARTOS_TAR_KULCS = "l0_tartosTar"; // "igen" | "nem"

function tarolo(win) {
  try {
    return win && win.localStorage ? win.localStorage : null;
  } catch (e) {
    return null;
  }
}

function olvas(t, kulcs) {
  try {
    return t ? t.getItem(kulcs) : null;
  } catch (e) {
    return null;
  }
}

function ir(t, kulcs, ertek) {
  try {
    if (t) t.setItem(kulcs, ertek);
  } catch (e) {
    /* nem kritikus: a tároló nem írható */
  }
}

function torol(t, kulcs) {
  try {
    if (t) t.removeItem(kulcs);
  } catch (e) {
    /* nem kritikus */
  }
}

const EMAIL_MINTA = /^[^\s@]{1,64}@[^\s@]{1,255}$/;

/** A legutóbbi e-mail-cím a Google `login_hint`-hez; közös eszközön vagy ismeretlen / hibás értéknél null. */
export function utolsoEmail(mod, win = typeof globalThis !== "undefined" ? globalThis : {}) {
  if (mod === "kozos") return null;
  const v = olvas(tarolo(win), EMAIL_KULCS);
  return v && EMAIL_MINTA.test(v) ? v : null;
}

/** A Google-szolgáltató paraméterei: a megjegyzett e-mail (ha van), egyébként üres. */
export function googleParameterek(mod, win) {
  const email = utolsoEmail(mod, win);
  return email ? { login_hint: email } : {};
}

/** A tag maga lép ki: ez NEM munkamenet-elvesztés. */
export function kilepesJelol(win = typeof globalThis !== "undefined" ? globalThis : {}) {
  ir(tarolo(win), KILEPETT_KULCS, "1");
}

/**
 * A hitelesítési állapot változása (a main.js watchAuthState-ből).
 *  - van felhasználó: megjegyezzük, hogy volt belépés (és saját eszközön az e-mailt); a "kilépett" jelzés törlődik;
 *  - nincs felhasználó, de volt belépés, és a tag nem lépett ki: a munkamenet elveszett → feljegyezzük (időpont, darabszám).
 * Közös eszközön semmit nem őrzünk (a törlődő munkamenet ott szándékos).
 * @returns {"belepett"|"elveszett"|"kilepett"|"semmi"}
 */
export function hitelesitesValtozott(user, mod, now = Date.now(), win = typeof globalThis !== "undefined" ? globalThis : {}) {
  const t = tarolo(win);
  if (mod === "kozos") {
    torol(t, EMAIL_KULCS);
    torol(t, VOLT_BELEPES_KULCS);
    torol(t, KILEPETT_KULCS);
    return "semmi";
  }
  if (user) {
    ir(t, VOLT_BELEPES_KULCS, "1");
    torol(t, KILEPETT_KULCS);
    if (user.email && EMAIL_MINTA.test(String(user.email))) ir(t, EMAIL_KULCS, String(user.email).trim().toLowerCase());
    return "belepett";
  }
  const volt = olvas(t, VOLT_BELEPES_KULCS) === "1";
  const kilepett = olvas(t, KILEPETT_KULCS) === "1";
  torol(t, VOLT_BELEPES_KULCS);
  torol(t, KILEPETT_KULCS);
  if (!volt) return "semmi";
  if (kilepett) return "kilepett";
  let db = 0;
  try {
    const regi = JSON.parse(olvas(t, ELVESZETT_KULCS) || "null");
    db = regi && Number.isInteger(regi.db) ? regi.db : 0;
  } catch (e) {
    db = 0;
  }
  ir(t, ELVESZETT_KULCS, JSON.stringify({ at: now, db: db + 1 }));
  return "elveszett";
}

/** A feljegyzett munkamenet-elvesztés: { at, db } vagy null. */
export function elvesztesOlvas(win = typeof globalThis !== "undefined" ? globalThis : {}) {
  try {
    const v = JSON.parse(olvas(tarolo(win), ELVESZETT_KULCS) || "null");
    return v && Number.isFinite(v.at) ? { at: v.at, db: Number.isInteger(v.db) ? v.db : 1 } : null;
  } catch (e) {
    return null;
  }
}

/**
 * Tartós tároló kérése. A böngésző dönthet: "igen" (megkapta), "nem" (elutasította / nem támogatott). Az eredmény megmarad az eszközön.
 * @returns {Promise<"igen"|"nem">}
 */
export async function tarolotKer(win = typeof globalThis !== "undefined" ? globalThis : {}) {
  const t = tarolo(win);
  let eredmeny = "nem";
  try {
    const s = win.navigator && win.navigator.storage;
    if (s && typeof s.persisted === "function") {
      if (await s.persisted()) eredmeny = "igen";
      else if (typeof s.persist === "function" && (await s.persist())) eredmeny = "igen";
    }
  } catch (e) {
    eredmeny = "nem";
  }
  ir(t, TARTOS_TAR_KULCS, eredmeny);
  return eredmeny;
}

/** Az utoljára mért tartós-tároló eredmény ("igen" / "nem"), vagy null, ha még nem mértük. */
export function tartosTarOlvas(win = typeof globalThis !== "undefined" ? globalThis : {}) {
  const v = olvas(tarolo(win), TARTOS_TAR_KULCS);
  return v === "igen" || v === "nem" ? v : null;
}
